import { apiGet, apiPost, requireApiData } from '@/lib/api/client';

export type GoLiveState = 'setup' | 'soft_live' | 'live' | 'demo';
export type SetupProfileId = 'blank' | 'india_smb' | 'india_tech';

export type ReadinessStepId =
  | 'shadow_org'
  | 'enums'
  | 'departments'
  | 'locations'
  | 'designations'
  | 'leave_types'
  | 'attendance_settings';

export type SetupPackMeta = {
  id: string;
  name: string;
  description: string;
  kind: 'atomic' | 'profile';
  includes?: string[];
  optional?: boolean;
};

export type ReadinessStep = {
  id: ReadinessStepId;
  label: string;
  required: boolean;
  complete: boolean;
  detail: string;
};

export type OrgSetupReadiness = {
  orgId: string;
  goLiveState: GoLiveState;
  hardGatesEnabled: boolean;
  profileId: SetupProfileId | null;
  percentComplete: number;
  requiredComplete: boolean;
  canGoLive: boolean;
  steps: ReadinessStep[];
  appliedPacks: Array<{
    packId: string;
    appliedAt: string;
    appliedBy: string;
    counts: Record<string, number>;
  }>;
  gates: {
    leaveApplications: boolean;
    selfOnboardInvites: boolean;
    attendanceCheckIn: boolean;
  };
};

export type ApplyPackResult = {
  packId: string;
  atomicPacks: string[];
  counts: Record<string, number>;
  readiness: OrgSetupReadiness;
};

export async function listSetupPacks(): Promise<SetupPackMeta[]> {
  const res = await apiGet<SetupPackMeta[]>('/org-setup/packs');
  return requireApiData(res.data, 'Failed to load setup packs');
}

export async function getOrgSetupReadiness(): Promise<OrgSetupReadiness> {
  const res = await apiGet<OrgSetupReadiness>('/org-setup/readiness');
  return requireApiData(res.data, 'Failed to load setup readiness');
}

export async function applySetupProfile(profileId: SetupProfileId): Promise<ApplyPackResult> {
  const res = await apiPost<ApplyPackResult>('/org-setup/apply-profile', { profileId });
  return requireApiData(res.data, 'Failed to apply setup profile');
}

export async function applySetupPack(packId: string): Promise<ApplyPackResult> {
  const res = await apiPost<ApplyPackResult>('/org-setup/apply-pack', { packId });
  return requireApiData(res.data, 'Failed to apply setup pack');
}

export async function markOrgGoLive(): Promise<{ readiness: OrgSetupReadiness }> {
  const res = await apiPost<{ readiness: OrgSetupReadiness }>('/org-setup/go-live', {});
  return requireApiData(res.data, 'Failed to mark go-live');
}

export async function setOrgSetupHardGates(enabled: boolean): Promise<{ hardGatesEnabled: boolean }> {
  const res = await apiPost<{ hardGatesEnabled: boolean }>('/org-setup/hard-gates', { enabled });
  return requireApiData(res.data, 'Failed to update hard gates');
}
